import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useMatch, useNavigate } from 'react-router-dom';

const workflowSteps = [
  { id: 1, title: 'Add Trial', done: true },
  { id: 2, title: 'Add Trial Content', done: true },
  { id: 3, title: 'Add Sponsor to Trial', done: true },
  { id: 4, title: 'Add Sites', done: false },
  { id: 5, title: 'Add Recruitment Target per Site', done: false },
  { id: 6, title: 'Add/Invite Community Support per Site', done: false },
  { id: 7, title: 'Add Managers', done: false },
  { id: 8, title: 'Manager-in-the-Loop Message Board', done: false }
];

const pendingConnects = [
  { patientId: 'PAT-10021', trial: 'NCT05432789', site: 'Miami Cancer Center', mustMatch: 'Pass', score: 91, consent: 'Authorized' },
  { patientId: 'PAT-10342', trial: 'NCT05432789', site: 'Tampa Oncology', mustMatch: 'Pass', score: 84, consent: 'Authorized' },
  { patientId: 'PAT-10485', trial: 'NCT05987123', site: 'Orlando Breast Institute', mustMatch: 'Fail', score: 79, consent: 'Authorized' }
];

const sponsorNetwork = [
  { id: 'SP-001', name: 'Merck Oncology', focus: 'Immunotherapy', contact: 'oncology.partnerships@merck.com', status: 'Open' },
  { id: 'SP-002', name: 'Pfizer Oncology', focus: 'HR+ / Targeted Therapy', contact: 'trial.ops@pfizer.com', status: 'Connected' },
  { id: 'SP-003', name: 'Genentech Breast Team', focus: 'TNBC / ADC', contact: 'study.support@genentech.com', status: 'Open' }
];

const communityPartnerNetwork = [
  { id: 'CP-001', name: 'CancerCare Transport Network', service: 'Transportation', county: 'Miami-Dade', status: 'Open' },
  { id: 'CP-002', name: 'Breast Support Alliance', service: 'Patient Navigation', county: 'Broward', status: 'Connected' },
  { id: 'CP-003', name: 'Florida Nutrition Access', service: 'Nutrition Support', county: 'Palm Beach', status: 'Open' }
];

const aiDrafts = [
  {
    id: 'MSG-901',
    patientId: 'PAT-10021',
    topic: 'Eligibility question',
    participantMessage: 'I am interested in this trial. I had surgery 5 months ago and I want to know if I may still qualify.',
    aiDraft: 'Based on your shared profile, you appear to meet key criteria. Your trial manager will confirm final eligibility and next steps.',
    status: 'Needs Review'
  },
  {
    id: 'MSG-902',
    patientId: 'PAT-10342',
    topic: 'Visit schedule',
    participantMessage: 'Can you tell me how often I need to come to the site and if transportation support is available?',
    aiDraft: 'Your site coordinator can provide visit timing and transportation options. We can connect you once the manager approves.',
    status: 'Needs Review'
  }
];

const notificationsInterestedPatients = [
  { id: 'NP-001', trial: 'NCT05432789', patientId: 'PAT-10512', patientName: 'Mia R.', interestAt: 'Today, 9:14 AM', status: 'New Interest' },
  { id: 'NP-002', trial: 'NCT05987123', patientId: 'PAT-10519', patientName: 'Rosa L.', interestAt: 'Today, 8:02 AM', status: 'New Interest' },
  { id: 'NP-003', trial: 'NCT06123456', patientId: 'PAT-10534', patientName: 'Elena M.', interestAt: 'Yesterday, 6:41 PM', status: 'Needs Review' }
];

const notificationsPatientMessages = [
  { id: 'NM-001', trial: 'NCT05432789', patientId: 'PAT-10512', patientName: 'Mia R.', subject: 'Eligibility timeline', message: 'I am interested and wanted to confirm if prior radiation affects my eligibility.', receivedAt: 'Today, 9:19 AM', status: 'Unread' },
  { id: 'NM-002', trial: 'NCT05987123', patientId: 'PAT-10519', patientName: 'Rosa L.', subject: 'Travel support', message: 'Do you provide any transportation help for weekly visits?', receivedAt: 'Today, 8:17 AM', status: 'Unread' },
  { id: 'NM-003', trial: 'NCT06123456', patientId: 'PAT-10534', patientName: 'Elena M.', subject: 'Document upload', message: 'I uploaded my pathology report and wanted to confirm it was received.', receivedAt: 'Yesterday, 6:52 PM', status: 'New Reply' }
];

const recruitmentMetrics = {
  activeTrials: 7,
  openSites: 19,
  qualifiedQueue: 63,
  connectAcceptanceRate: 72
};

const mockManagedTrials = [
  { id: 'TR-001', nct: 'NCT05432789', title: 'Pembrolizumab in Early-Stage Breast Cancer', role: 'Primary Manager', status: 'Recruiting', sites: 4, createdBy: 'dr.smith@mammotrial.dev' },
  { id: 'TR-002', nct: 'NCT05987123', title: 'Targeted Therapy Combination Trial', role: 'Co-Manager', status: 'Setup', sites: 2, createdBy: 'coordinator@mammotrial.dev' },
  { id: 'TR-003', nct: 'NCT06123456', title: 'Adjuvant Precision Oncology Study', role: 'Reviewer', status: 'Active', sites: 3, createdBy: 'reviewer@mammotrial.dev' }
];

const trialCreatorRoles = new Set([
  'principal_investigator',
  'coordinator',
  'recruiter',
  'data_manager',
  'clinician',
  'researcher',
  'admin'
]);

const creatorEmailAllowlist = new Set([
  'dr.smith@mammotrial.dev'
]);

const ClinicalPortalContent = ({ activeTab = 0 }) => {
  const { user, getUserCategory } = useAuth();
  const navigate = useNavigate();
  const trialHomeMatch = useMatch('/trial-home/:trialId');
  const [selectedTrialId, setSelectedTrialId] = useState(() => localStorage.getItem('selectedTrialId') || null);
  const [managedTrials, setManagedTrials] = useState(mockManagedTrials);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createMode, setCreateMode] = useState('manual');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importNctId, setImportNctId] = useState('');
  const [manualForm, setManualForm] = useState({
    title: '',
    nct: '',
    sponsor: '',
    status: 'Setup',
    sites: 1
  });
  const [trialHomeTab, setTrialHomeTab] = useState(0);
  const [managerReviewQueue, setManagerReviewQueue] = useState(() => (
    aiDrafts.map((draft) => ({
      ...draft,
      managerResponse: draft.aiDraft || '',
      approved: false,
      sentAt: null,
      status: draft.aiDraft ? 'Needs Review' : 'Awaiting AI Draft'
    }))
  ));
  const completedSteps = workflowSteps.filter((step) => step.done).length;
  const normalizedRole = (user?.primary_role || user?.role || '').toLowerCase();
  const normalizedEmail = (user?.email || '').toLowerCase();
  const isTrialManagementGroup = getUserCategory && getUserCategory() === 'trials_team';
  const requireTrialSelection = isTrialManagementGroup;
  const canCreateTrial = trialCreatorRoles.has(normalizedRole) || creatorEmailAllowlist.has(normalizedEmail);
  const myManagedTrials = managedTrials;
  const routeTrialId = trialHomeMatch?.params?.trialId || null;
  const isTrialHomeRoute = !!routeTrialId;
  const selectedTrial = managedTrials.find((trial) => trial.id === (routeTrialId || selectedTrialId)) || null;
  const isManagerReviewTab = activeTab === 4;
  const isNotificationsTab = activeTab === 5;
  const isSelectedTrialCreator = !!selectedTrial && selectedTrial.createdBy === normalizedEmail;
  const sharedTableContainerSx = {
    borderRadius: 2,
    borderColor: 'divider',
    overflow: 'hidden'
  };
  const sharedTableSx = {
    '& .MuiTableCell-head': {
      bgcolor: 'action.hover',
      color: 'text.secondary',
      fontWeight: 700
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      bgcolor: 'action.hover'
    },
    '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
      borderBottom: 'none'
    }
  };

  useEffect(() => {
    if (selectedTrialId) {
      localStorage.setItem('selectedTrialId', selectedTrialId);
    } else {
      localStorage.removeItem('selectedTrialId');
    }
  }, [selectedTrialId]);

  useEffect(() => {
    if (routeTrialId && routeTrialId !== selectedTrialId) {
      setSelectedTrialId(routeTrialId);
      setTrialHomeTab(0);
    }
  }, [routeTrialId, selectedTrialId]);

  const resetCreateDialog = () => {
    setCreateMode('manual');
    setImporting(false);
    setImportError('');
    setImportNctId('');
    setManualForm({
      title: '',
      nct: '',
      sponsor: '',
      status: 'Setup',
      sites: 1
    });
  };

  const handleOpenCreateDialog = () => {
    resetCreateDialog();
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleImportFromClinicalTrials = async () => {
    const nctId = importNctId.trim().toUpperCase();
    if (!nctId) {
      setImportError('Enter a valid NCT ID (example: NCT05432789).');
      return;
    }

    try {
      setImporting(true);
      setImportError('');

      const response = await fetch(`https://clinicaltrials.gov/api/v2/studies/${encodeURIComponent(nctId)}`);
      if (!response.ok) {
        throw new Error('Study not found on ClinicalTrials.gov.');
      }

      const data = await response.json();
      const study = data?.study || (Array.isArray(data?.studies) ? data.studies[0] : null) || data;
      const identification = study?.protocolSection?.identificationModule || {};
      const statusModule = study?.protocolSection?.statusModule || {};
      const contactsModule = study?.protocolSection?.contactsLocationsModule || {};
      const sponsorModule = study?.protocolSection?.sponsorCollaboratorsModule || {};

      const title = identification?.briefTitle || identification?.officialTitle || '';
      const sponsor = sponsorModule?.leadSponsor?.name || '';
      const status = statusModule?.overallStatus || 'Setup';
      const sites = Array.isArray(contactsModule?.locations) ? contactsModule.locations.length : 1;

      setManualForm({
        title,
        nct: nctId,
        sponsor,
        status,
        sites: sites > 0 ? sites : 1
      });
      setCreateMode('manual');
    } catch (error) {
      setImportError(error?.message || 'Unable to import trial from ClinicalTrials.gov.');
    } finally {
      setImporting(false);
    }
  };

  const handleCreateTrial = () => {
    if (!manualForm.title.trim()) {
      setImportError('Trial title is required.');
      return;
    }

    const nextId = `TR-${String(Date.now()).slice(-6)}`;
    const newTrial = {
      id: nextId,
      nct: manualForm.nct.trim() || 'Pending NCT',
      title: manualForm.title.trim(),
      role: 'Primary Manager',
      status: manualForm.status.trim() || 'Setup',
      sites: Number(manualForm.sites) > 0 ? Number(manualForm.sites) : 1,
      sponsor: manualForm.sponsor.trim() || 'Not assigned',
      createdBy: normalizedEmail || 'unknown'
    };

    setManagedTrials((previous) => [newTrial, ...previous]);
    setSelectedTrialId(newTrial.id);
    setTrialHomeTab(0);
    setCreateDialogOpen(false);
    navigate(`/trial-home/${newTrial.id}`);
  };

  const handleOpenTrial = (trialId) => {
    setSelectedTrialId(trialId);
    setTrialHomeTab(0);
    navigate(`/trial-home/${trialId}`);
  };

  const handleGenerateAiDraft = (draftId) => {
    setManagerReviewQueue((previous) => previous.map((draft) => {
      if (draft.id !== draftId || draft.status === 'Sent') {
        return draft;
      }

      const generated = `Thank you for your question about ${draft.topic.toLowerCase()}. Based on the information available, a trial manager will review your details and follow up with specific next steps, including scheduling and support options when applicable.`;

      return {
        ...draft,
        managerResponse: generated,
        approved: false,
        status: 'Needs Review'
      };
    }));
  };

  const handleEditManagerResponse = (draftId, value) => {
    setManagerReviewQueue((previous) => previous.map((draft) => {
      if (draft.id !== draftId || draft.status === 'Sent') {
        return draft;
      }

      const trimmed = value.trim();
      return {
        ...draft,
        managerResponse: value,
        approved: false,
        status: trimmed ? 'Needs Review' : 'Awaiting AI Draft'
      };
    }));
  };

  const handleApproveResponse = (draftId) => {
    setManagerReviewQueue((previous) => previous.map((draft) => {
      if (draft.id !== draftId || draft.status === 'Sent' || !draft.managerResponse.trim()) {
        return draft;
      }

      return {
        ...draft,
        approved: true,
        status: 'Approved'
      };
    }));
  };

  const handleSendResponse = (draftId) => {
    setManagerReviewQueue((previous) => previous.map((draft) => {
      if (draft.id !== draftId || !draft.approved || !draft.managerResponse.trim() || draft.status === 'Sent') {
        return draft;
      }

      return {
        ...draft,
        status: 'Sent',
        sentAt: new Date().toLocaleString()
      };
    }));
  };

  const renderTrialHomePage = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Trial Home
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure this trial using dedicated function tabs.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Recruitment and invitations open only when setup is complete, mCODE mapping is valid, match criteria pass, and patient consent authorization is present.
      </Alert>

      {!isSelectedTrialCreator ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Only the trial creator can perform setup actions in this Trial Home.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={trialHomeTab}
          onChange={(event, nextTab) => setTrialHomeTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1 }}
        >
          <Tab label="Add Trial Content" />
          <Tab label="Add Sponsor to Trial" />
          <Tab label="Add Sites" />
          <Tab label="Add Recruitment Target per Site" />
          <Tab label="Add/Invite Community Support per Site" />
          <Tab label="Add Managers" />
          <Tab label="Manager-in-the-Loop Message Board" />
        </Tabs>
      </Paper>

      {trialHomeTab === 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Add Trial Content</Typography>
              <Chip size="small" color="success" label="Done" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Trial synopsis, eligibility summary, and protocol overview are already captured.
            </Typography>
            <Button variant="contained" disabled={!isSelectedTrialCreator}>Edit Trial Content</Button>
          </CardContent>
        </Card>
      )}

      {trialHomeTab === 1 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Add Sponsor to Trial</Typography>
              <Chip size="small" color="success" label="Done" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sponsor linkage is available and can be updated by the creator.
            </Typography>
            <Button variant="contained" disabled={!isSelectedTrialCreator}>Manage Sponsor</Button>
          </CardContent>
        </Card>
      )}

      {trialHomeTab === 2 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Add Sites</Typography>
              <Chip size="small" label="Pending" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add participating sites and assign site coordinators.
            </Typography>
            <Button variant="contained" disabled={!isSelectedTrialCreator}>Add Sites</Button>
          </CardContent>
        </Card>
      )}

      {trialHomeTab === 3 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Add Recruitment Target per Site</Typography>
              <Chip size="small" label="Pending" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define target patient counts for each active site.
            </Typography>
            <Button variant="contained" disabled={!isSelectedTrialCreator}>Set Recruitment Targets</Button>
          </CardContent>
        </Card>
      )}

      {trialHomeTab === 4 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Add/Invite Community Support per Site</Typography>
              <Chip size="small" label="Pending" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Invite transportation, navigation, and nutrition support partners by site.
            </Typography>
            <Button variant="contained" disabled={!isSelectedTrialCreator}>Invite Community Support</Button>
          </CardContent>
        </Card>
      )}

      {trialHomeTab === 5 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Add Managers</Typography>
              <Chip size="small" label="Pending" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add co-managers and reviewers for trial operations.
            </Typography>
            <Button variant="contained" disabled={!isSelectedTrialCreator}>Add Managers</Button>
          </CardContent>
        </Card>
      )}

      {trialHomeTab === 6 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Manager-in-the-Loop Message Board</Typography>
              <Chip size="small" label="Pending" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review AI-generated patient responses, edit, approve, and send.
            </Typography>
            <Button variant="contained" disabled={!isSelectedTrialCreator}>Open Message Board</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Recruitment Workflow Checklist</Typography>
            <Chip label={`${completedSteps}/${workflowSteps.length} complete`} color="primary" />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
                {workflowSteps.slice(1).map((step) => (
              <Grid item xs={12} md={6} key={step.id}>
                <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {step.id}. {step.title}
                  </Typography>
                  <Chip
                    size="small"
                    label={step.done ? 'Done' : 'Pending'}
                    color={step.done ? 'success' : 'default'}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  const renderWorkflowView = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Clinical Trial Manager Home
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open a trial from Trial Setup to go to its Trial Home page where functional tabs are available.
      </Typography>
      {selectedTrial ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
              Current Trial
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedTrial.nct} — {selectedTrial.title}
            </Typography>
            <Button variant="contained" onClick={() => navigate(`/trial-home/${selectedTrial.id}`)}>
              Open Trial Home
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );

  const renderTrialSelectionView = () => (
    <Box>
      <Card sx={{ mb: 3 }} variant="outlined">
        <CardContent>
          <TableContainer component={Paper} variant="outlined" sx={sharedTableContainerSx}>
            <Table size="small" sx={sharedTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Trial</TableCell>
                  <TableCell>NCT</TableCell>
                  <TableCell>Your Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sites</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myManagedTrials.map((trial) => (
                  <TableRow key={trial.id} hover onClick={() => handleOpenTrial(trial.id)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ fontWeight: 600 }}>{trial.title}</TableCell>
                    <TableCell>{trial.nct}</TableCell>
                    <TableCell>
                      <Chip size="small" label={trial.role} />
                    </TableCell>
                    <TableCell>{trial.status}</TableCell>
                    <TableCell>{trial.sites}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" onClick={(event) => { event.stopPropagation(); handleOpenTrial(trial.id); }}>Open Trial</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            {canCreateTrial ? (
              <Button variant="contained" onClick={handleOpenCreateDialog}>Create Trial</Button>
            ) : (
              <Chip label="Creator permission required" size="small" />
            )}
          </Box>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Connect with Sponsor</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={sharedTableContainerSx}>
                    <Table size="small" sx={sharedTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Sponsor</TableCell>
                          <TableCell>Focus</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sponsorNetwork.map((sponsor) => (
                          <TableRow key={sponsor.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{sponsor.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{sponsor.contact}</Typography>
                            </TableCell>
                            <TableCell>{sponsor.focus}</TableCell>
                            <TableCell>
                              <Chip size="small" label={sponsor.status} color={sponsor.status === 'Connected' ? 'success' : 'default'} />
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" disabled={sponsor.status === 'Connected'}>
                                Send Connect Request
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" size="small">
                      Add Sponsor
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Connect with Community Partner</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={sharedTableContainerSx}>
                    <Table size="small" sx={sharedTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Partner</TableCell>
                          <TableCell>Service</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {communityPartnerNetwork.map((partner) => (
                          <TableRow key={partner.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{partner.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{partner.county}</Typography>
                            </TableCell>
                            <TableCell>{partner.service}</TableCell>
                            <TableCell>
                              <Chip size="small" label={partner.status} color={partner.status === 'Connected' ? 'success' : 'default'} />
                            </TableCell>
                            <TableCell align="right">
                              <Button size="small" variant="outlined" disabled={partner.status === 'Connected'}>
                                Send Connect Request
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" size="small">
                      Add Community Partner to My Network
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  const renderTrialSetupView = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Trial Setup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create/import trial, map mCODE criteria, then configure sponsor, sites, and recruitment targets.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Trial Intake</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="contained">Add Trial</Button>
                <Button variant="outlined">Import from ClinicalTrials.gov</Button>
                <Button variant="outlined">Link NCT ID</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>mCODE & Eligibility Rules</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="contained" color="secondary">Map to mCODE</Button>
                <Button variant="outlined">Set Must-Match Rules</Button>
                <Button variant="outlined">Set Percentage Threshold</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Site Operations</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="contained">Add Sites</Button>
                <Button variant="outlined">Set Recruitment Target per Site</Button>
                <Button variant="outlined">Invite Community Support per Site</Button>
                <Button variant="outlined">Add Managers</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );

  const renderPatientConnectView = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Patient Connect Queue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Only patients with must-match pass, threshold score, and consent authorization can be contacted.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Trial</TableCell>
              <TableCell>Site</TableCell>
              <TableCell>Must Match</TableCell>
              <TableCell>Match %</TableCell>
              <TableCell>Consent</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingConnects.map((row) => {
              const canSend = row.mustMatch === 'Pass' && row.consent === 'Authorized' && row.score >= 80;
              return (
                <TableRow key={row.patientId}>
                  <TableCell>{row.patientId}</TableCell>
                  <TableCell>{row.trial}</TableCell>
                  <TableCell>{row.site}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.mustMatch} color={row.mustMatch === 'Pass' ? 'success' : 'error'} />
                  </TableCell>
                  <TableCell>{row.score}%</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.consent} color={row.consent === 'Authorized' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="contained" disabled={!canSend}>
                      Send Connect Request
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderMetricsView = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Recruitment Metrics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track engagement-to-enrollment outcomes across trials, sites, and qualified patient queues.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'Active Trials', value: recruitmentMetrics.activeTrials },
          { label: 'Open Sites', value: recruitmentMetrics.openSites },
          { label: 'Qualified Queue', value: recruitmentMetrics.qualifiedQueue },
          { label: 'Connect Acceptance', value: `${recruitmentMetrics.connectAcceptanceRate}%` }
        ].map((metric) => (
          <Grid item xs={12} sm={6} md={3} key={metric.label}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">{metric.label}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{metric.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

    </Box>
  );

  const renderManagerReviewView = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Manager Review Board (AI Drafts)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review incoming messages from interested trial participants, generate AI responses, edit as needed, then approve and send.
      </Typography>

      <Card>
        <CardContent>
          {managerReviewQueue.map((draft) => (
            <Paper key={draft.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">{draft.patientId} • {draft.topic}</Typography>
                <Chip
                  size="small"
                  label={draft.status}
                  color={draft.status === 'Sent' ? 'success' : draft.status === 'Approved' ? 'primary' : 'warning'}
                />
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Participant Message
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {draft.participantMessage}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                AI Draft / Manager Response
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={draft.managerResponse}
                disabled={draft.status === 'Sent'}
                onChange={(event) => handleEditManagerResponse(draft.id, event.target.value)}
                sx={{ mb: 2 }}
              />

              {draft.sentAt ? (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 2 }}>
                  Sent at {draft.sentAt}
                </Typography>
              ) : null}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleGenerateAiDraft(draft.id)}
                  disabled={draft.status === 'Sent'}
                >
                  Generate AI Draft
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleApproveResponse(draft.id)}
                  disabled={draft.status === 'Sent' || !draft.managerResponse.trim()}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => handleSendResponse(draft.id)}
                  disabled={!draft.approved || draft.status === 'Sent'}
                >
                  Send Response
                </Button>
              </Box>
            </Paper>
          ))}
        </CardContent>
      </Card>
    </Box>
  );

  const renderNotificationsView = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Notifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        New patient interest across trials and new messages from patients.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            New Patients Interested by Trial
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={sharedTableContainerSx}>
            <Table size="small" sx={sharedTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Trial</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Patient ID</TableCell>
                  <TableCell>Interest Time</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationsInterestedPatients.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.trial}</TableCell>
                    <TableCell>{item.patientName}</TableCell>
                    <TableCell>{item.patientId}</TableCell>
                    <TableCell>{item.interestAt}</TableCell>
                    <TableCell>
                      <Chip size="small" label={item.status} color={item.status === 'Needs Review' ? 'warning' : 'primary'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            New Messages from Patients
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={sharedTableContainerSx}>
            <Table size="small" sx={sharedTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Trial</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationsPatientMessages.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.trial}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.patientName}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.patientId}</Typography>
                    </TableCell>
                    <TableCell>{item.subject}</TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {item.message}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.receivedAt}</TableCell>
                    <TableCell>
                      <Chip size="small" label={item.status} color="warning" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth="md">
        <DialogTitle>Create Trial</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <Button
              variant={createMode === 'manual' ? 'contained' : 'outlined'}
              onClick={() => setCreateMode('manual')}
            >
              Manual Entry
            </Button>
            <Button
              variant={createMode === 'ctgov' ? 'contained' : 'outlined'}
              onClick={() => setCreateMode('ctgov')}
            >
              Import from ClinicalTrials.gov
            </Button>
          </Box>

          {createMode === 'ctgov' && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>ClinicalTrials.gov Import</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="NCT ID"
                  placeholder="NCT05432789"
                  value={importNctId}
                  onChange={(event) => setImportNctId(event.target.value)}
                />
                <Button variant="contained" onClick={handleImportFromClinicalTrials} disabled={importing}>
                  {importing ? <CircularProgress size={18} color="inherit" /> : 'Fetch Trial'}
                </Button>
              </Box>
            </Paper>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trial Title"
                value={manualForm.title}
                onChange={(event) => setManualForm((previous) => ({ ...previous, title: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="NCT ID"
                value={manualForm.nct}
                onChange={(event) => setManualForm((previous) => ({ ...previous, nct: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sponsor"
                value={manualForm.sponsor}
                onChange={(event) => setManualForm((previous) => ({ ...previous, sponsor: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Status"
                value={manualForm.status}
                onChange={(event) => setManualForm((previous) => ({ ...previous, status: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Sites"
                value={manualForm.sites}
                onChange={(event) => setManualForm((previous) => ({ ...previous, sites: event.target.value }))}
              />
            </Grid>
          </Grid>

          {importError ? (
            <Alert severity="warning" sx={{ mt: 2 }}>{importError}</Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTrial}>Create Trial</Button>
        </DialogActions>
      </Dialog>

      {requireTrialSelection && !selectedTrial && !isManagerReviewTab && !isNotificationsTab && !isTrialHomeRoute ? renderTrialSelectionView() : null}

      {isTrialHomeRoute && !selectedTrial ? (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/trial-management')}>
              Back to Trial Manager Home
            </Button>
          }
        >
          Trial not found. Please open a valid trial from Trial Setup.
        </Alert>
      ) : null}

      {requireTrialSelection && !selectedTrial && !isManagerReviewTab && !isNotificationsTab && !isTrialHomeRoute ? null : (
        <>
      {selectedTrial && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => { setSelectedTrialId(null); setTrialHomeTab(0); navigate('/trial-management'); }}>
              Switch Trial
            </Button>
          }
        >
          Active Trial: <strong>{selectedTrial.nct}</strong> — {selectedTrial.title}
        </Alert>
      )}

      {isTrialHomeRoute && selectedTrial ? renderTrialHomePage() : null}
      {!isTrialHomeRoute && (!requireTrialSelection || !selectedTrial || activeTab === 0) ? renderWorkflowView() : null}
      {!isTrialHomeRoute && (!requireTrialSelection || selectedTrial) && activeTab === 1 && renderTrialSetupView()}
      {!isTrialHomeRoute && (!requireTrialSelection || selectedTrial) && activeTab === 2 && renderPatientConnectView()}
      {!isTrialHomeRoute && (!requireTrialSelection || selectedTrial) && activeTab === 3 && renderMetricsView()}
      {!isTrialHomeRoute && (!requireTrialSelection || selectedTrial) && activeTab === 4 && renderManagerReviewView()}
      {!isTrialHomeRoute && (((!requireTrialSelection || selectedTrial) || isNotificationsTab) && activeTab === 5) && renderNotificationsView()}
        </>
      )}
    </Box>
  );
};

export default ClinicalPortalContent;
